/**
 * RunResultSection — the run-record page's Result section (26742): one
 * sentence, plus any `*_warning` strings beside it. Deliberately NOT a JSON
 * dump — that level of detail belongs to the Evidence section.
 */
import { resultSentence } from "./resultSentence";
import { extractResultWarnings } from "../resultWarnings";
import type { WorkflowResultBody } from "../result.types";
import { WarningAcknowledgementList } from "../components/WarningAcknowledgementList";
import type { WarningAckState } from "../hooks/useWarningAcknowledgements";

export interface RunResultSectionProps {
  status: string | null;
  /** `get_run_decisions().summary` — `null`/absent when the tool hasn't run. */
  summary?: string | null;
  result: WorkflowResultBody | null;
  /**
   * Warning acknowledgement (2026-10-06): the host's live state. When the run
   * publishes lines, each renders with its own Acknowledge instead of the plain
   * box; absent or `items: null` keeps the box (an API that predates it).
   */
  acknowledgements?: WarningAckState | null;
}

export function RunResultSection({
  status,
  summary,
  result,
  acknowledgements = null,
}: RunResultSectionProps) {
  const sentence = resultSentence({ summary, status, result });
  const warnings = extractResultWarnings(
    result as Record<string, unknown> | null,
  );

  return (
    <div className="flex flex-col gap-2" data-testid="run-result-section">
      <p
        className="text-sm text-primary-gray-light-900"
        data-testid="run-result-sentence"
      >
        {sentence}
      </p>
      {acknowledgements?.items && acknowledgements.items.length > 0 ? (
        <WarningAcknowledgementList ack={acknowledgements} />
      ) : warnings.length > 0 ? (
        <div
          className="rounded border border-primary-warning-300 bg-primary-warning-50 px-2 py-1.5 text-xs text-primary-warning-800"
          data-testid="run-result-warnings"
        >
          {warnings.map((w, i) => (
            <p key={i} className="leading-snug">
              {w}
            </p>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default RunResultSection;
